import { useState, useEffect, useCallback, useMemo } from 'react';
import { useConnector } from '../contexts/ConnectorContext';
import type { AssetData } from '../types/connector.types';
import type { PersonOption } from '../types/timesheet.types';
import { DatePickerInput } from './DatePickerInput';

type PeopleTab = 'people' | 'assets' | 'hierarchy';
type ViewMode = 'flat' | 'grouped';

interface PersonItem {
  id: string;
  name: string;
  email: string;
  dept: string;
  role: string;
  mgrName: string;
  mgrEmail: string;
  bakName: string;
  bakEmail: string;
  supName: string;
  supEmail: string;
  costType: string;
  hourlyRate: string;
  exempt: boolean;
  exemptStart: string;
  exemptEnd: string;
  mgmt: boolean;
  active: boolean;
  purchaseApprovalLimit: number | null;
  isChemicalApprover: boolean;
  isOrderMgmtGroup: boolean;
  isCFO: boolean;
  canApproveOnBehalfOfCEO: boolean;
}

interface AssetOwner {
  email: string;
  name: string;
}

interface AssetItem {
  id: string;
  name: string;
  desc: string;
  owners: AssetOwner[];
  active: boolean;
}

interface PersonForm {
  name: string;
  email: string;
  dept: string;
  role: string;
  mgrName: string;
  mgrEmail: string;
  bakName: string;
  bakEmail: string;
  supName: string;
  supEmail: string;
  costType: string;
  hourlyRate: string;
  exempt: boolean;
  exemptStart: string;
  exemptEnd: string;
  mgmt: boolean;
  active: boolean;
  purchaseApprovalLimit: string;
  isChemicalApprover: boolean;
  isOrderMgmtGroup: boolean;
  isCFO: boolean;
  canApproveOnBehalfOfCEO: boolean;
}

interface AssetForm {
  name: string;
  desc: string;
  owners: AssetOwner[];
}

const ROLE_CLASS: Record<string, string> = {
  'Colleague': 'role-colleague',
  'Line Manager': 'role-manager',
  'PMO Lead / Super Admin': 'role-pmo',
  'System Administrator': 'role-sysadmin',
};

const AV_PALETTE = ['purple', 'amber', 'pink', 'teal', 'green', 'blue', 'indigo', 'rose'];

function avatarColor(email: string): string {
  let h = 0;
  for (let i = 0; i < email.length; i++) h = (h * 31 + email.charCodeAt(i)) & 0xffff;
  return AV_PALETTE[h % AV_PALETTE.length];
}

function initials(name: string) {
  return name.split(' ').map(s => s[0] || '').slice(0, 2).join('').toUpperCase();
}

function ApproverCell({ name, email }: { name: string; email: string }) {
  if (!name) return <span style={{ color: 'var(--text-dim)', fontSize: 13 }}>—</span>;
  return (
    <div style={{ minWidth: 0, overflow: 'hidden' }}>
      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
      {email && <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: "'DM Mono',monospace", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email}</div>}
    </div>
  );
}

const EMPTY_PERSON_FORM: PersonForm = {
  name: '', email: '', dept: '', role: '',
  mgrName: '', mgrEmail: '', bakName: '', bakEmail: '', supName: '', supEmail: '',
  costType: '', hourlyRate: '', exempt: false, exemptStart: '', exemptEnd: '',
  mgmt: false, active: true,
  purchaseApprovalLimit: '', isChemicalApprover: false, isOrderMgmtGroup: false, isCFO: false, canApproveOnBehalfOfCEO: false,
};

const EMPTY_ASSET_FORM: AssetForm = { name: '', desc: '', owners: [] };

export function PeopleAdmin() {
  const connector = useConnector();

  const [people, setPeople] = useState<PersonItem[]>([]);
  const [assets, setAssets] = useState<AssetItem[]>([]);
  const [tab, setTab] = useState<PeopleTab>('people');
  const [peopleSearch, setPeopleSearch] = useState('');
  const [assetSearch, setAssetSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [roleOptions, setRoleOptions] = useState<string[]>([]);
  const [costTypeOptions, setCostTypeOptions] = useState<string[]>([]);
  const [mgrOptions, setMgrOptions] = useState<PersonOption[]>([]);
  const [bakOptions, setBakOptions] = useState<PersonOption[]>([]);
  const [supOptions, setSupOptions] = useState<PersonOption[]>([]);

  const [personModalOpen, setPersonModalOpen] = useState(false);
  const [editPersonId, setEditPersonId] = useState<string | null>(null);
  const [personForm, setPersonForm] = useState<PersonForm>(EMPTY_PERSON_FORM);
  const [personFormError, setPersonFormError] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [assetModalOpen, setAssetModalOpen] = useState(false);
  const [editAssetId, setEditAssetId] = useState<string | null>(null);
  const [assetForm, setAssetForm] = useState<AssetForm>(EMPTY_ASSET_FORM);
  const [assetFormError, setAssetFormError] = useState(false);
  const [assetSaveError, setAssetSaveError] = useState<string | null>(null);

  // Hierarchy tab state
  const [hvTab, setHvTab] = useState<'staff' | 'assets'>('staff');
  const [hvViewMode, setHvViewMode] = useState<ViewMode>('flat');
  const [hvSearch, setHvSearch] = useState('');
  const [hvShowInactive, setHvShowInactive] = useState(false);
  const [hvRoleFilter, setHvRoleFilter] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [persons, assetsList, roles, costTypes, mgrs, baks, sups] = await Promise.all([
        connector.getPeople(),
        connector.getAssets(),
        connector.getRoleOptions(),
        connector.getCostTypeOptions(),
        connector.getLineManagerOptions(),
        connector.getBackupApproverOptions(),
        connector.getSuperApproverOptions(),
      ]);
      setPeople(persons.map(p => ({
        id: p.id,
        name: p.fullName,
        email: p.m365Email,
        dept: p.department,
        role: p.role,
        mgrName: p.lineManager,
        mgrEmail: p.lineManagerEmail,
        bakName: p.backupApprover,
        bakEmail: p.backupApproverEmail,
        supName: p.superApprover,
        supEmail: p.superApproverEmail,
        costType: p.costType ?? 'Fixed',
        hourlyRate: p.hourlyRate != null ? String(p.hourlyRate) : '',
        exempt: p.exemptFromReminders ?? false,
        exemptStart: p.exemptionStart ?? '',
        exemptEnd: p.exemptionEnd ?? '',
        mgmt: p.isTimesheetMgmtGroup,
        active: p.active,
        purchaseApprovalLimit: p.purchaseApprovalLimit ?? null,
        isChemicalApprover: p.isChemicalApprover,
        isOrderMgmtGroup: p.isOrderMgmtGroup,
        isCFO: p.isCFO,
        canApproveOnBehalfOfCEO: p.canApproveOnBehalfOfCEO,
      })).sort((a, b) => a.name.localeCompare(b.name)));
      setAssets(assetsList.map(a => ({
        id: a.id,
        name: a.title,
        desc: a.description,
        owners: a.responsiblePeopleEmails.map((email, i) => ({
          email,
          name: a.responsiblePeopleNames[i] ?? '',
        })),
        active: a.active,
      })));
      setRoleOptions(roles);
      setCostTypeOptions(costTypes);
      setMgrOptions(mgrs);
      setBakOptions(baks);
      setSupOptions(sups);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [connector]);

  useEffect(() => { void loadData(); }, [loadData]);

  const filteredPeople = people.filter(p =>
    !peopleSearch ||
    p.name.toLowerCase().includes(peopleSearch.toLowerCase()) ||
    p.email.toLowerCase().includes(peopleSearch.toLowerCase())
  ).sort((a, b) => a.name.localeCompare(b.name));

  const filteredAssets = assets.filter(a =>
    !assetSearch || a.name.toLowerCase().includes(assetSearch.toLowerCase())
  );

  const activePeople = people.filter(p => p.active);

  // ── Hierarchy derived values ──────────────────────────────────────────────
  const staffByEmail = useMemo(() => {
    const map = new Map<string, PersonItem>();
    for (const p of people) map.set(p.email.toLowerCase(), p);
    return map;
  }, [people]);

  const hvRoles = useMemo(() => {
    const set = new Set<string>();
    for (const p of people) if (p.role) set.add(p.role);
    return Array.from(set).sort();
  }, [people]);

  const hvFilteredStaff = useMemo(() => {
    let list = hvShowInactive ? people : people.filter(p => p.active);
    if (hvRoleFilter) list = list.filter(p => p.role === hvRoleFilter);
    if (hvSearch) {
      const q = hvSearch.toLowerCase();
      list = list.filter(p =>
        p.name.toLowerCase().includes(q) ||
        p.email.toLowerCase().includes(q) ||
        p.dept.toLowerCase().includes(q) ||
        p.mgrName.toLowerCase().includes(q)
      );
    }
    return list;
  }, [people, hvSearch, hvShowInactive, hvRoleFilter]);

  const hvFilteredAssets = useMemo(() => {
    let list = hvShowInactive ? assets : assets.filter(a => a.active);
    if (hvSearch) {
      const q = hvSearch.toLowerCase();
      list = list.filter(a =>
        a.name.toLowerCase().includes(q) ||
        a.owners.some(o => o.name.toLowerCase().includes(q) || o.email.toLowerCase().includes(q))
      );
    }
    return list;
  }, [assets, hvSearch, hvShowInactive]);

  const groupedStaff = useMemo(() => {
    const groups = new Map<string, PersonItem[]>();
    for (const p of hvFilteredStaff) {
      const key = p.mgrName || '— No Line Manager Assigned —';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(p);
    }
    return Array.from(groups.entries()).sort((a, b) => {
      if (a[0].startsWith('—')) return 1;
      if (b[0].startsWith('—')) return -1;
      return a[0].localeCompare(b[0]);
    });
  }, [hvFilteredStaff]);

  function handleHvTabChange(next: 'staff' | 'assets') {
    setHvTab(next);
    setHvSearch('');
    setHvRoleFilter('');
  }

  function StaffRow({ p }: { p: PersonItem }) {
    return (
      <div className={`admin-row hvw-staff-grid${!p.active ? ' inactive' : ''}`}>
        <div className="col-person">
          <div className={`pav ${avatarColor(p.email)}`}>{initials(p.name)}</div>
          <div>
            <div className="p-name">
              {p.name}
              {p.mgmt && <span className="star" title="Timesheet Management Group">★</span>}
            </div>
            <div className="p-dept">{p.dept || <span style={{ color: 'var(--text-dim)' }}>—</span>}</div>
          </div>
        </div>
        <div>
          {p.role
            ? <span className={`role-badge ${ROLE_CLASS[p.role] ?? 'role-colleague'}`}>{p.role}</span>
            : <span style={{ color: 'var(--text-dim)', fontSize: 13 }}>—</span>
          }
        </div>
        <div><ApproverCell name={p.mgrName} email={p.mgrEmail} /></div>
        <div><ApproverCell name={p.bakName} email={p.bakEmail} /></div>
        <div><ApproverCell name={p.supName} email={p.supEmail} /></div>
      </div>
    );
  }
  // ─────────────────────────────────────────────────────────────────────────

  function openAddPerson() {
    setEditPersonId(null);
    setPersonForm({ ...EMPTY_PERSON_FORM, role: roleOptions[0] ?? '' });
    setPersonFormError(false);
    setSaveError(null);
    setPersonModalOpen(true);
  }

  function openEditPerson(id: string) {
    const p = people.find(x => x.id === id);
    if (!p) return;
    setEditPersonId(id);
    setPersonForm({
      name: p.name, email: p.email, dept: p.dept, role: p.role,
      mgrName: p.mgrName, mgrEmail: p.mgrEmail,
      bakName: p.bakName, bakEmail: p.bakEmail,
      supName: p.supName, supEmail: p.supEmail,
      costType: p.costType, hourlyRate: p.hourlyRate, exempt: p.exempt, exemptStart: p.exemptStart, exemptEnd: p.exemptEnd,
      mgmt: p.mgmt, active: p.active,
      purchaseApprovalLimit: p.purchaseApprovalLimit != null ? String(p.purchaseApprovalLimit) : '',
      isChemicalApprover: p.isChemicalApprover,
      isOrderMgmtGroup: p.isOrderMgmtGroup,
      isCFO: p.isCFO,
      canApproveOnBehalfOfCEO: p.canApproveOnBehalfOfCEO,
    });
    setPersonFormError(false);
    setSaveError(null);
    setPersonModalOpen(true);
  }

  async function savePerson() {
    if (!personForm.name.trim() || !personForm.email.trim()) { setPersonFormError(true); return; }
    setSaving(true);
    setSaveError(null);
    try {
      const data = {
        fullName: personForm.name.trim(),
        m365Email: personForm.email.trim(),
        department: personForm.dept.trim(),
        role: personForm.role,
        lineManager: personForm.mgrName,
        lineManagerEmail: personForm.mgrEmail,
        backupApprover: personForm.bakName,
        backupApproverEmail: personForm.bakEmail,
        superApprover: personForm.supName,
        superApproverEmail: personForm.supEmail,
        costType: personForm.costType,
        hourlyRate: personForm.hourlyRate !== '' ? Number(personForm.hourlyRate) : null,
        exemptFromReminders: personForm.exempt,
        exemptionStart: personForm.exemptStart || null,
        exemptionEnd: personForm.exemptEnd || null,
        isTimesheetMgmtGroup: personForm.mgmt,
        active: personForm.active,
        purchaseApprovalLimit: personForm.purchaseApprovalLimit !== '' ? Number(personForm.purchaseApprovalLimit) : null,
        isChemicalApprover: personForm.isChemicalApprover,
        isOrderMgmtGroup: personForm.isOrderMgmtGroup,
        isCFO: personForm.isCFO,
        canApproveOnBehalfOfCEO: personForm.canApproveOnBehalfOfCEO,
      };
      if (editPersonId) {
        await connector.updatePerson(editPersonId, data);
        setPeople(prev => prev.map(p => p.id === editPersonId ? {
          ...p,
          name: data.fullName, email: data.m365Email, dept: data.department,
          role: data.role,
          mgrName: data.lineManager, mgrEmail: data.lineManagerEmail,
          bakName: data.backupApprover, bakEmail: data.backupApproverEmail,
          supName: data.superApprover, supEmail: data.superApproverEmail,
          costType: data.costType ?? p.costType,
          hourlyRate: data.hourlyRate != null ? String(data.hourlyRate) : '',
          exempt: data.exemptFromReminders ?? p.exempt,
          exemptStart: data.exemptionStart ?? '',
          exemptEnd: data.exemptionEnd ?? '',
          mgmt: data.isTimesheetMgmtGroup, active: data.active,
          purchaseApprovalLimit: data.purchaseApprovalLimit ?? null,
          isChemicalApprover: data.isChemicalApprover ?? p.isChemicalApprover,
          isOrderMgmtGroup: data.isOrderMgmtGroup ?? p.isOrderMgmtGroup,
          isCFO: data.isCFO ?? p.isCFO,
          canApproveOnBehalfOfCEO: data.canApproveOnBehalfOfCEO ?? p.canApproveOnBehalfOfCEO,
        } : p));
      } else {
        const created = await connector.createPerson(data);
        setPeople(prev => [...prev, {
          id: created.id,
          name: created.fullName, email: created.m365Email, dept: created.department,
          role: created.role,
          mgrName: created.lineManager, mgrEmail: created.lineManagerEmail,
          bakName: created.backupApprover, bakEmail: created.backupApproverEmail,
          supName: created.superApprover, supEmail: created.superApproverEmail,
          costType: created.costType ?? '',
          hourlyRate: created.hourlyRate != null ? String(created.hourlyRate) : '',
          exempt: created.exemptFromReminders ?? false,
          exemptStart: created.exemptionStart ?? '',
          exemptEnd: created.exemptionEnd ?? '',
          mgmt: created.isTimesheetMgmtGroup, active: created.active,
          purchaseApprovalLimit: created.purchaseApprovalLimit ?? null,
          isChemicalApprover: created.isChemicalApprover,
          isOrderMgmtGroup: created.isOrderMgmtGroup,
          isCFO: created.isCFO,
          canApproveOnBehalfOfCEO: created.canApproveOnBehalfOfCEO,
        }]);
      }
      setPersonModalOpen(false);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Failed to save person');
    } finally {
      setSaving(false);
    }
  }

  function selectOption(
    field: 'mgr' | 'bak' | 'sup',
    options: PersonOption[],
    email: string
  ) {
    const found = options.find(o => o.email === email);
    const name = found?.name ?? '';
    if (field === 'mgr') setPersonForm(f => ({ ...f, mgrEmail: email, mgrName: name }));
    else if (field === 'bak') setPersonForm(f => ({ ...f, bakEmail: email, bakName: name }));
    else setPersonForm(f => ({ ...f, supEmail: email, supName: name }));
  }

  function openAddAsset() {
    setEditAssetId(null);
    setAssetForm(EMPTY_ASSET_FORM);
    setAssetFormError(false);
    setAssetSaveError(null);
    setAssetModalOpen(true);
  }

  function openEditAsset(id: string) {
    const a = assets.find(x => x.id === id);
    if (!a) return;
    setEditAssetId(id);
    setAssetForm({ name: a.name, desc: a.desc, owners: a.owners.map(o => ({ ...o })) });
    setAssetFormError(false);
    setAssetSaveError(null);
    setAssetModalOpen(true);
  }

  async function saveAsset() {
    if (!assetForm.name.trim() || assetForm.owners.length === 0) { setAssetFormError(true); return; }
    setSaving(true);
    setAssetSaveError(null);
    try {
      const data: AssetData = {
        title: assetForm.name.trim(),
        description: assetForm.desc.trim(),
        responsiblePerson: assetForm.owners[0]?.email ?? '',
        responsiblePersonEmail: assetForm.owners[0]?.email ?? '',
        responsiblePersonName: assetForm.owners[0]?.name ?? '',
        responsiblePeopleEmails: assetForm.owners.map(o => o.email),
        responsiblePeopleNames: assetForm.owners.map(o => o.name),
        active: true,
      };
      if (editAssetId) {
        await connector.updateAsset(editAssetId, data);
        setAssets(prev => prev.map(a => a.id === editAssetId ? {
          ...a, name: data.title, desc: data.description,
          owners: assetForm.owners.map(o => ({ ...o })),
        } : a));
      } else {
        const created = await connector.createAsset(data);
        setAssets(prev => [...prev, {
          id: created.id, name: created.title, desc: created.description,
          owners: created.responsiblePeopleEmails.map((email, i) => ({
            email,
            name: created.responsiblePeopleNames[i] ?? '',
          })),
          active: created.active,
        }]);
      }
      setAssetModalOpen(false);
    } catch (e) {
      setAssetSaveError(e instanceof Error ? e.message : 'Failed to save asset');
    } finally {
      setSaving(false);
    }
  }

  async function toggleAsset(id: string) {
    const a = assets.find(x => x.id === id);
    if (!a) return;
    const next = !a.active;
    setAssets(prev => prev.map(x => x.id === id ? { ...x, active: next } : x));
    try {
      await connector.updateAsset(id, { active: next });
    } catch {
      setAssets(prev => prev.map(x => x.id === id ? { ...x, active: a.active } : x));
    }
  }

  if (loading) {
    return (
      <>
        <div className="ts-topbar">
          <div className="topbar-l">
            <div className="crumb"><span>Admin</span><span className="sep">/</span><span className="cur">People &amp; Hierarchy</span></div>
          </div>
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
          Loading…
        </div>
      </>
    );
  }

  if (loadError) {
    return (
      <>
        <div className="ts-topbar">
          <div className="topbar-l">
            <div className="crumb"><span>Admin</span><span className="sep">/</span><span className="cur">People &amp; Hierarchy</span></div>
          </div>
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
          <div style={{ color: 'var(--error, #ef4444)', fontSize: 14 }}>{loadError}</div>
          <button className="btn btn-ghost" onClick={() => void loadData()}>Retry</button>
        </div>
      </>
    );
  }

  const activeStaffCount = people.filter(p => p.active).length;
  const activeAssetCount = assets.filter(a => a.active).length;

  return (
    <>
      <div className="ts-topbar">
        <div className="topbar-l">
          <div className="crumb">
            <span>Admin</span><span className="sep">/</span><span className="cur">People &amp; Hierarchy</span>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            <button className={`filter-tab${tab === 'people' ? ' active' : ''}`} onClick={() => setTab('people')}>People</button>
            <button className={`filter-tab${tab === 'assets' ? ' active' : ''}`} onClick={() => setTab('assets')}>Assets</button>
            <button className={`filter-tab${tab === 'hierarchy' ? ' active' : ''}`} onClick={() => setTab('hierarchy')}>Hierarchy</button>
          </div>
        </div>
        <div className="topbar-r" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {tab === 'people' && (
            <button className="btn btn-ghost" onClick={openAddPerson}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 13, height: 13, strokeWidth: 2 }}><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
              Add Person
            </button>
          )}
          {tab === 'assets' && (
            <button className="btn btn-ghost" onClick={openAddAsset}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 13, height: 13, strokeWidth: 2 }}><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
              Add Asset
            </button>
          )}
          {tab === 'hierarchy' && (
            <>
              {hvTab === 'staff' && (
                <button
                  className="btn btn-ghost"
                  style={{ fontSize: 12 }}
                  onClick={() => setHvViewMode(v => v === 'flat' ? 'grouped' : 'flat')}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 13, height: 13, strokeWidth: 2 }}>
                    {hvViewMode === 'flat'
                      ? <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></>
                      : <><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></>
                    }
                  </svg>
                  {hvViewMode === 'flat' ? 'Group by Manager' : 'Flat View'}
                </button>
              )}
              <button
                className="btn btn-ghost"
                style={{ fontSize: 12 }}
                onClick={() => setHvShowInactive(v => !v)}
              >
                {hvShowInactive ? 'Hide Inactive' : 'Show Inactive'}
              </button>
            </>
          )}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', background: 'var(--bg)', padding: '20px 28px' }}>

        {tab === 'people' && (
          <div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
              <div className="search-wrap">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                <input placeholder="Search people..." value={peopleSearch} onChange={e => setPeopleSearch(e.target.value)} />
              </div>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{filteredPeople.length} people</span>
            </div>
            <div className="tbl-scroll-wrap">
            <div className="admin-hdr people-grid">
              <div>Person</div>
              <div>M365 Email</div>
              <div>Line Manager</div>
              <div>Backup Approver</div>
              <div>Super Approver</div>
              <div style={{ textAlign: 'center' }}>Role</div>
              <div style={{ textAlign: 'right' }}>Edit</div>
            </div>
            {filteredPeople.map(p => (
              <div key={p.id} className={`admin-row people-grid${!p.active ? ' inactive' : ''}`}>
                <div className="col-person">
                  <div className={`pav ${avatarColor(p.email)}`}>{initials(p.name)}</div>
                  <div>
                    <div className="p-name">{p.name}{p.mgmt && <span className="star">★</span>}</div>
                    <div className="p-dept">{p.dept}</div>
                  </div>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: "'DM Mono',monospace" }}>{p.email}</div>
                <div style={{ fontSize: 12.5, color: p.mgrName ? 'var(--text)' : 'var(--text-dim)' }}>{p.mgrName || '—'}</div>
                <div style={{ fontSize: 12.5, color: p.bakName ? 'var(--text)' : 'var(--text-dim)' }}>{p.bakName || '—'}</div>
                <div style={{ fontSize: 12.5, color: p.supName ? 'var(--text)' : 'var(--text-dim)' }}>{p.supName || '—'}</div>
                <div style={{ textAlign: 'center' }}>
                  <span className={`role-badge ${ROLE_CLASS[p.role] ?? 'role-colleague'}`}>{p.role}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button className="icon-btn" onClick={() => openEditPerson(p.id)} title="Edit">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                  </button>
                </div>
              </div>
            ))}
            </div>{/* tbl-scroll-wrap */}
          </div>
        )}

        {tab === 'assets' && (
          <div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
              <div className="search-wrap">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                <input placeholder="Search assets..." value={assetSearch} onChange={e => setAssetSearch(e.target.value)} />
              </div>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{filteredAssets.length} assets</span>
            </div>
            <div className="info-box blue">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
              Assets don&apos;t have M365 accounts. The responsible person logs time on their behalf. The asset name appears as a row in their timesheet.
            </div>
            <div className="tbl-scroll-wrap">
            <div className="admin-hdr asset-grid">
              <div>Asset Name</div>
              <div>Description</div>
              <div>Responsible Person</div>
              <div style={{ textAlign: 'center' }}>Status</div>
              <div style={{ textAlign: 'right' }}>Edit</div>
            </div>
            {filteredAssets.map(a => (
                <div key={a.id} className="admin-row asset-grid">
                  <div>
                    <div className="task-name" style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 14, height: 14, strokeWidth: 1.8, color: 'var(--text-dim)', flexShrink: 0 }}><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" /></svg>
                      {a.name}
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{a.desc}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {a.owners.length === 0
                      ? <span style={{ color: 'var(--text-dim)', fontSize: 12 }}>Unassigned</span>
                      : a.owners.map(o => (
                        <div key={o.email} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                          <div className={`pav ${avatarColor(o.email)}`} style={{ width: 22, height: 22, fontSize: '9px', flexShrink: 0 }}>{initials(o.name || o.email)}</div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.name || o.email}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: "'DM Mono',monospace", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.email}</div>
                          </div>
                        </div>
                      ))
                    }
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <button className={`toggle-btn ${a.active ? 'on' : 'off'}`} onClick={() => void toggleAsset(a.id)}>
                      {a.active ? 'Active' : 'Inactive'}
                    </button>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button className="icon-btn" onClick={() => openEditAsset(a.id)} title="Edit">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                    </button>
                  </div>
                </div>
            ))}
            </div>{/* tbl-scroll-wrap */}
          </div>
        )}

        {tab === 'hierarchy' && (
          <div>
            {/* Sub-tabs */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 14 }}>
              <button className={`filter-tab${hvTab === 'staff' ? ' active' : ''}`} onClick={() => handleHvTabChange('staff')}>
                Staff ({activeStaffCount})
              </button>
              <button className={`filter-tab${hvTab === 'assets' ? ' active' : ''}`} onClick={() => handleHvTabChange('assets')}>
                Assets ({activeAssetCount})
              </button>
            </div>

            {/* Search + role filters */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
              <div className="search-wrap">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  placeholder={hvTab === 'staff' ? 'Search staff, dept, manager…' : 'Search assets or owner…'}
                  value={hvSearch}
                  onChange={e => setHvSearch(e.target.value)}
                />
              </div>
              <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                {hvTab === 'staff' ? hvFilteredStaff.length : hvFilteredAssets.length} results
              </span>
              {hvTab === 'staff' && hvRoles.length > 0 && (
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                  <button
                    className={`filter-tab${hvRoleFilter === '' ? ' active' : ''}`}
                    style={{ fontSize: 11, padding: '2px 10px' }}
                    onClick={() => setHvRoleFilter('')}
                  >All roles</button>
                  {hvRoles.map(r => (
                    <button
                      key={r}
                      className={`filter-tab${hvRoleFilter === r ? ' active' : ''}`}
                      style={{ fontSize: 11, padding: '2px 10px' }}
                      onClick={() => setHvRoleFilter(hvRoleFilter === r ? '' : r)}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* ── Staff sub-tab ── */}
            {hvTab === 'staff' && (
              <div className="hvw-table-wrap">
                <div className="admin-hdr hvw-staff-grid">
                  <div>Person</div>
                  <div>Role</div>
                  <div>Line Manager</div>
                  <div>Backup Approver</div>
                  <div>Super Approver</div>
                </div>

                {hvFilteredStaff.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)', fontSize: 13 }}>
                    No staff match the current filters.
                  </div>
                )}

                {hvViewMode === 'flat' && hvFilteredStaff.map(p => <StaffRow key={p.id} p={p} />)}

                {hvViewMode === 'grouped' && groupedStaff.map(([mgrName, members]) => (
                  <div key={mgrName} style={{ marginBottom: 12 }}>
                    <div className="hvw-group-hdr">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 13, height: 13, flexShrink: 0, strokeWidth: 2 }}>
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                        <circle cx="9" cy="7" r="4" />
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                      </svg>
                      <span>{mgrName}</span>
                      <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 11, marginLeft: 6 }}>
                        {members.length} {members.length === 1 ? 'person' : 'people'}
                      </span>
                    </div>
                    {members.map(p => <StaffRow key={p.id} p={p} />)}
                  </div>
                ))}
              </div>
            )}

            {/* ── Assets sub-tab ── */}
            {hvTab === 'assets' && (
              <div className="hvw-table-wrap">
                <div className="info-box blue" style={{ marginBottom: 14 }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                  Approval chain for each asset is derived from the responsible person&apos;s People record.
                  If the responsible person is not found in the People list the chain will show as unset.
                </div>

                <div className="admin-hdr hvw-asset-grid">
                  <div>Asset</div>
                  <div>Responsible Person</div>
                  <div>Line Manager</div>
                  <div>Backup Approver</div>
                  <div>Super Approver</div>
                </div>

                {hvFilteredAssets.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)', fontSize: 13 }}>
                    No assets match the current filters.
                  </div>
                )}

                {hvFilteredAssets.map(a => {
                  const person = staffByEmail.get((a.owners[0]?.email ?? '').toLowerCase());
                  return (
                    <div key={a.id} className={`admin-row hvw-asset-grid${!a.active ? ' inactive' : ''}`}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{a.name}</div>
                        {a.desc && (
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{a.desc}</div>
                        )}
                      </div>
                      <div>
                        {a.owners.length > 0
                          ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                              {a.owners.map(o => (
                                <div key={o.email}>
                                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{o.name || o.email}</div>
                                  <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: "'DM Mono',monospace" }}>{o.email}</div>
                                </div>
                              ))}
                            </div>
                          )
                          : <span style={{ color: 'var(--text-dim)', fontSize: 13 }}>—</span>
                        }
                      </div>
                      <div><ApproverCell name={person?.mgrName ?? ''} email={person?.mgrEmail ?? ''} /></div>
                      <div><ApproverCell name={person?.bakName ?? ''} email={person?.bakEmail ?? ''} /></div>
                      <div><ApproverCell name={person?.supName ?? ''} email={person?.supEmail ?? ''} /></div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Person Modal ─────────────────────────────────────────────────────────── */}
      <div className={`adm-modal-bg${personModalOpen ? ' open' : ''}`}>
        <div className="adm-modal wide">
          <div className="adm-modal-hdr">
            <span className="adm-modal-hdr-title">{editPersonId ? 'Edit Person' : 'Add Person'}</span>
            <button className="adm-modal-x" onClick={() => setPersonModalOpen(false)}>×</button>
          </div>
          <div className="adm-modal-body">
            <div className="form-row cols2">
              <div className="form-field">
                <label>Full Name <span className="req">*</span></label>
                <input className={`finput${personFormError && !personForm.name.trim() ? ' error' : ''}`} value={personForm.name} onChange={e => setPersonForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. James Hargreaves" />
              </div>
              <div className="form-field">
                <label>M365 Email <span className="req">*</span></label>
                <input className={`finput${personFormError && !personForm.email.trim() ? ' error' : ''}`} type="email" value={personForm.email} onChange={e => setPersonForm(f => ({ ...f, email: e.target.value }))} placeholder="james@evove.co.uk" />
              </div>
            </div>
            <div className="form-row cols2">
              <div className="form-field">
                <label>Department</label>
                <input className="finput" value={personForm.dept} onChange={e => setPersonForm(f => ({ ...f, dept: e.target.value }))} placeholder="e.g. Engineering" />
              </div>
              <div className="form-field">
                <label>Role</label>
                <select className="finput" value={personForm.role} onChange={e => setPersonForm(f => ({ ...f, role: e.target.value }))}>
                  <option value="">— Select Role —</option>
                  {roleOptions.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>
            <div className="form-field">
              <label>Line Manager</label>
              <select className="finput" value={personForm.mgrEmail} onChange={e => selectOption('mgr', mgrOptions, e.target.value)}>
                <option value="">— None —</option>
                {mgrOptions.map(o => <option key={o.email} value={o.email}>{o.name}</option>)}
              </select>
            </div>
            <div className="form-row cols2">
              <div className="form-field">
                <label>Backup Approver</label>
                <select className="finput" value={personForm.bakEmail} onChange={e => selectOption('bak', bakOptions, e.target.value)}>
                  <option value="">— None —</option>
                  {bakOptions.map(o => <option key={o.email} value={o.email}>{o.name}</option>)}
                </select>
              </div>
              <div className="form-field">
                <label>Super Approver</label>
                <select className="finput" value={personForm.supEmail} onChange={e => selectOption('sup', supOptions, e.target.value)}>
                  <option value="">— None —</option>
                  {supOptions.map(o => <option key={o.email} value={o.email}>{o.name}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row cols2" style={{ paddingTop: 10, borderTop: '1px solid var(--border)', marginTop: 4 }}>
              <div className="form-field">
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  Cost Type
                  <span style={{ fontSize: 10, background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 4, padding: '1px 5px', color: 'var(--text-muted)', letterSpacing: '0.04em' }}>Admin only</span>
                </label>
                <select className="finput" value={personForm.costType} onChange={e => setPersonForm(f => ({ ...f, costType: e.target.value }))}>
                  {costTypeOptions.length === 0
                    ? <option value={personForm.costType}>{personForm.costType}</option>
                    : costTypeOptions.map(ct => <option key={ct} value={ct}>{ct}</option>)
                  }
                </select>
              </div>
              <div className="form-field" style={{display: "none"}}>
                <label>
                  {personForm.costType === 'Fixed' ? 'Annual Salary (£)' : 'Hourly Rate (£)'}
                  <span style={{ fontSize: 10, background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 4, padding: '1px 5px', color: 'var(--text-muted)', marginLeft: 6 }}>Admin only</span>
                </label>
                <input className="finput" type="number" min="0" step="0.01" value={personForm.hourlyRate} onChange={e => setPersonForm(f => ({ ...f, hourlyRate: e.target.value }))} placeholder={personForm.costType === 'Fixed' ? 'e.g. 50000' : 'e.g. 45.00'} />
              </div>
            </div>
            <div style={{ paddingTop: 10, borderTop: '1px solid var(--border)', marginTop: 4 }}>
              <div className="fcheck" style={{ marginBottom: 10 }}>
                <input type="checkbox" id="per-exempt-chk" checked={personForm.exempt} onChange={e => setPersonForm(f => ({ ...f, exempt: e.target.checked }))} />
                <label htmlFor="per-exempt-chk">Exempt from automated reminders</label>
              </div>
              {personForm.exempt && (
                <div>
                  <div className="form-row cols2">
                    <div className="form-field">
                      <label>Exemption Start</label>
                      <DatePickerInput value={personForm.exemptStart} onChange={v => setPersonForm(f => ({ ...f, exemptStart: v }))} />
                    </div>
                    <div className="form-field">
                      <label>Exemption End</label>
                      <DatePickerInput value={personForm.exemptEnd} onChange={v => setPersonForm(f => ({ ...f, exemptEnd: v }))} />
                    </div>
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4 }}>⚠ Exempt employees will not receive reminder notifications during this period. Timesheet may be completed by their manager.</div>
                </div>
              )}
            </div>
            <div className="form-row cols2" style={{ marginTop: 10 }}>
              <div className="fcheck">
                <input type="checkbox" id="per-mgmt-chk" checked={personForm.mgmt} onChange={e => setPersonForm(f => ({ ...f, mgmt: e.target.checked }))} />
                <label htmlFor="per-mgmt-chk">Super Admin (Timesheet Mgmt)</label>
              </div>
              <div className="fcheck">
                <input type="checkbox" id="per-active-chk" checked={personForm.active} onChange={e => setPersonForm(f => ({ ...f, active: e.target.checked }))} />
                <label htmlFor="per-active-chk">Active</label>
              </div>
            </div>
            <div style={{ paddingTop: 10, borderTop: '1px solid var(--border)', marginTop: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: 8 }}>Ordering Permissions</div>
              <div className="form-field" style={{ marginBottom: 10 }}>
                <label>
                  Purchase Approval Limit (£)
                  <span style={{ fontSize: 10, background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 4, padding: '1px 5px', color: 'var(--text-muted)', marginLeft: 6 }}>Admin only</span>
                </label>
                <input
                  className="finput"
                  type="number"
                  min="0"
                  step="1"
                  placeholder="Leave blank for default £250"
                  value={personForm.purchaseApprovalLimit}
                  onChange={e => setPersonForm(f => ({ ...f, purchaseApprovalLimit: e.target.value }))}
                />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>Max order value this person can self-approve. Default £250 for all staff.</div>
              </div>
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                <div className="fcheck">
                  <input type="checkbox" id="per-chemapprover-chk" checked={personForm.isChemicalApprover} onChange={e => setPersonForm(f => ({ ...f, isChemicalApprover: e.target.checked }))} />
                  <label htmlFor="per-chemapprover-chk">Chemical Approver (COSHH)</label>
                </div>
                <div className="fcheck">
                  <input type="checkbox" id="per-ommgmt-chk" checked={personForm.isOrderMgmtGroup} onChange={e => setPersonForm(f => ({ ...f, isOrderMgmtGroup: e.target.checked }))} />
                  <label htmlFor="per-ommgmt-chk">Order Management Group (OM)</label>
                </div>
                <div className="fcheck">
                  <input type="checkbox" id="per-cfo-chk" checked={personForm.isCFO} onChange={e => setPersonForm(f => ({ ...f, isCFO: e.target.checked }))} />
                  <label htmlFor="per-cfo-chk">CFO (approves orders ≥£10k)</label>
                </div>
                <div className="fcheck">
                  <input type="checkbox" id="per-ceo-obo-chk" checked={personForm.canApproveOnBehalfOfCEO} onChange={e => setPersonForm(f => ({ ...f, canApproveOnBehalfOfCEO: e.target.checked }))} />
                  <label htmlFor="per-ceo-obo-chk">Can Approve on Behalf of CEO</label>
                </div>
              </div>
            </div>
            <div className={`ferr${personFormError ? ' show' : ''}`}>Name and email are required.</div>
            {saveError && <div className="ferr show">{saveError}</div>}
            <div className="adm-modal-footer">
              <button className="btn btn-ghost" onClick={() => setPersonModalOpen(false)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={() => void savePerson()} disabled={saving}>{saving ? 'Saving…' : 'Save Person'}</button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Asset Modal ──────────────────────────────────────────────────────────── */}
      <div className={`adm-modal-bg${assetModalOpen ? ' open' : ''}`}>
        <div className="adm-modal">
          <div className="adm-modal-hdr">
            <span className="adm-modal-hdr-title">{editAssetId ? 'Edit Asset' : 'Add Asset'}</span>
            <button className="adm-modal-x" onClick={() => setAssetModalOpen(false)}>×</button>
          </div>
          <div className="adm-modal-body">
            <div className="form-field">
              <label>Asset Name <span className="req">*</span></label>
              <input className={`finput${assetFormError && !assetForm.name.trim() ? ' error' : ''}`} value={assetForm.name} onChange={e => setAssetForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. CNC Machine 1" />
            </div>
            <div className="form-field">
              <label>Description</label>
              <input className="finput" value={assetForm.desc} onChange={e => setAssetForm(f => ({ ...f, desc: e.target.value }))} placeholder="e.g. Laser cutter, Building 2" />
            </div>
            <div className="form-field">
              <label>Responsible People <span className="req">*</span></label>
              {assetForm.owners.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {assetForm.owners.map((o, i) => (
                    <div key={o.email} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 20, padding: '3px 8px 3px 6px', fontSize: 12.5 }}>
                      <div className={`pav ${avatarColor(o.email)}`} style={{ width: 18, height: 18, fontSize: '8px', flexShrink: 0 }}>{initials(o.name || o.email)}</div>
                      <span style={{ color: 'var(--text)' }}>{o.name || o.email}</span>
                      <button
                        type="button"
                        onClick={() => setAssetForm(f => ({ ...f, owners: f.owners.filter((_, j) => j !== i) }))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0 0 0 2px', lineHeight: 1, fontSize: 15, display: 'flex', alignItems: 'center' }}
                        title="Remove"
                      >×</button>
                    </div>
                  ))}
                </div>
              )}
              <select
                className={`finput${assetFormError && assetForm.owners.length === 0 ? ' error' : ''}`}
                value=""
                onChange={e => {
                  if (!e.target.value) return;
                  const p = activePeople.find(x => x.email === e.target.value);
                  if (!p) return;
                  setAssetForm(f => ({ ...f, owners: [...f.owners, { email: p.email, name: p.name }] }));
                }}
              >
                <option value="">— Add person —</option>
                {activePeople.filter(p => !assetForm.owners.some(o => o.email === p.email)).map(p => (
                  <option key={p.email} value={p.email}>{p.name} ({p.email})</option>
                ))}
              </select>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4 }}>Each person logs time for this asset from their own timesheet. The first person listed is the primary responsible person.</div>
            </div>
            <div className={`ferr${assetFormError ? ' show' : ''}`}>Asset name and at least one responsible person are required.</div>
            {assetSaveError && <div className="ferr show">{assetSaveError}</div>}
            <div className="adm-modal-footer">
              <button className="btn btn-ghost" onClick={() => setAssetModalOpen(false)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={() => void saveAsset()} disabled={saving}>{saving ? 'Saving…' : 'Save Asset'}</button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .hvw-staff-grid {
          display: grid;
          grid-template-columns: minmax(160px,1fr) 120px 160px 160px 160px;
          gap: 0 12px;
          align-items: center;
        }
        .hvw-staff-grid > * { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .hvw-asset-grid {
          display: grid;
          grid-template-columns: minmax(160px,1fr) 180px 160px 160px 160px;
          gap: 0 12px;
          align-items: center;
        }
        .hvw-asset-grid > * { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .hvw-group-hdr {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 7px 12px;
          font-size: 12.5px;
          font-weight: 600;
          color: var(--text-secondary);
          background: var(--surface-raised, var(--surface));
          border: 1px solid var(--border);
          border-radius: 6px 6px 0 0;
          border-bottom: none;
          margin-top: 4px;
        }
        .hvw-group-hdr + .admin-row {
          border-top: none;
        }
      `}</style>
    </>
  );
}
